import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { ReservationCompare } from '../models/reservation-compare';
import { ReservationCompareStore } from '../service/reservation-compare-store';
import { LogService } from '../../shared/log/log.service';
import { EventStore } from '../service/event.store';
import { OnyxEvent } from '../models/event';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { PartyType } from '../models/party-type';
import { Party } from '../models/party';

@Injectable()
export class VendorTransactionResolver implements Resolve<any> {
  private previousRoute: string = null;

  constructor(private reservationCompareStore: ReservationCompareStore,
              private eventStore: EventStore,
              private translateService: TranslateService,
              private flashMessagesService: FlashMessagesService,
              private logService: LogService,
              private router: Router) {
    // This code is required in order to know the previous route in resolve.
    // Note that first the NavigationStart event will happen, then resolve() is called if the resolver is attached
    // to the route, then NavigationEnd happens.
    this.router.events
      .filter(e => e instanceof NavigationEnd)
      .subscribe((e: NavigationEnd) => {
        this.previousRoute = e.url;
      });
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<List<ReservationCompare>> {
    const id = route.params[ 'id' ];
    const eventPartyId = route.params[ 'eventPartyId' ];

    if (!id) {
      throw new Error(this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_TRANSACTIONS'));
    }

    if (eventPartyId) {
      return this.eventStore.getEvent(id).catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_TRANSACTIONS',
          `Error retrieving transactions: ${error}`);
        return Observable.throw(error);
      }).switchMap((event: OnyxEvent) => {
        if (this.onlySubscribers(event)) {
          return this.reservationCompareStore.getReservationsByParty(id, eventPartyId).first();
        } else if (this.onlyNonSubscriber(event)) {
          return this.reservationCompareStore.getRsvpsByParty(id, eventPartyId).first();
        } else {
          return this.reservationCompareStore.getReservationsAndRsvpsByParty(id, eventPartyId).first();
        }
      });
    } else {
      return this.eventStore.getEvent(id).catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_TRANSACTIONS',
          `Error retrieving transactions: ${error}`);
        return Observable.throw(error);
      }).switchMap((event: OnyxEvent) => {
        if (this.onlySubscribers(event)) {
          return this.reservationCompareStore.getReservations(id).first();
        } else if (this.onlyNonSubscriber(event)) {
          return this.reservationCompareStore.getRsvps(id).first();
        } else {
          return this.reservationCompareStore.getReservationsAndRsvps(id).first();
        }
      });
    }

  }

  private onlySubscribers(event: OnyxEvent): boolean {
    const subscriberProperties = this.getSubscribers(event);
    const nonSubscriberProperties = this.getNonSubscribers(event);
    return subscriberProperties && subscriberProperties.length > 0 && (!nonSubscriberProperties || nonSubscriberProperties.length === 0);
  }

  private onlyNonSubscriber(event: OnyxEvent): boolean {
    const subscriberProperties = this.getSubscribers(event);
    const nonSubscriberProperties = this.getNonSubscribers(event);
    return nonSubscriberProperties && nonSubscriberProperties.length > 0 && (!subscriberProperties || subscriberProperties.length === 0);
  }

  private getSubscribers(event: OnyxEvent): Party[] {
    return event.eventVendors
    .filter(vendor => vendor.partyType === PartyType.PROPERTY && vendor.payorType === 'PROPERTY' && vendor.subscriber);
  }

  private getNonSubscribers(event: OnyxEvent): Party[] {
    return event.eventVendors
    .filter(vendor => vendor.partyType === PartyType.PROPERTY && vendor.payorType === 'PROPERTY' && !vendor.subscriber);
  }

}






// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/overview/vendor-transaction.resolver.ts